import { useCallback, useRef, useState } from 'react';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { supplierApi } from '../api/supplierApi';
import { SupplierDetail } from '../types/supplier.types';
import { getItem } from '@/shared/utils/storage';
import { exportToExcel, getTimestampForFilename, ExcelColumn } from '@/shared/utils/excelExport';
import { isValidExcelFile, SupplierFilters } from "@/features/partner/supplierStatus/utils/supplierSelectionHelpers";
import { useSupplierList } from "@/features/partner/supplierStatus/hooks/useSupplierList";
import { useSupplierFilter } from "@/features/partner/supplierStatus/hooks/useSupplierFilter";

interface UseSupplierSelectionHandlersParams {
    onSupplierSelect: (supplierId: string, supplierName: string) => void;
    resetPagination: () => void;
}

interface UseSupplierSelectionHandlersReturn {
    suppliers: SupplierDetail[];
    allSuppliers: SupplierDetail[];
    loading: boolean;
    uploading: boolean;
    error: boolean;
    filters: SupplierFilters;
    fileInputRef: React.RefObject<HTMLInputElement | null>;
    setFilters: React.Dispatch<React.SetStateAction<SupplierFilters>>;
    fetchSuppliers: () => Promise<void>;
    handleSearch: () => void;
    handleQuickFilter: (newFilters: SupplierFilters) => void;
    handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    handleRowClick: (supplier: SupplierDetail) => void;
    handleUploadClick: () => void;
    handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
    handleRetry: () => void;
    handleExcelDownload: () => void;
}

export const useSupplierSelectionHandlers = ({
                                                 onSupplierSelect,
                                                 resetPagination,
                                             }: UseSupplierSelectionHandlersParams): UseSupplierSelectionHandlersReturn => {
    const { showToast } = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const { suppliers, allSuppliers, loading, error, setSuppliers, fetchSuppliers } = useSupplierList();
    const { filters, setFilters, handleSearch, handleQuickFilter, handleKeyDown } = useSupplierFilter({
        allSuppliers,
        setSuppliers,
        resetPagination,
    });

    const handleRowClick = useCallback(
        (supplier: SupplierDetail)=> {
            onSupplierSelect(supplier.id, supplier.name);
        },
        [onSupplierSelect]
    );

    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            if (!isValidExcelFile(file.name)) {
                showToast('엑셀 파일만 업로드 가능합니다.', { type: ToastType.ERROR });
                return;
            }

            setUploading(true);
            try {
                // localStorage에서 실제 사용자 ID 가져오기
                const userId = getItem<string>('USER_ID') || '';
                const result = await supplierApi.uploadGradeExcel(file, userId);
                if (result.success) {
                    showToast('협력사 등급이 업로드되었습니다.', { type: ToastType.SUCCESS });
                    await fetchSuppliers();
                } else {
                    showToast(result.message || '등급 업로드에 실패했습니다.', { type: ToastType.ERROR });
                }
            } catch {
                showToast('등급 업로드 중 오류가 발생했습니다.', { type: ToastType.ERROR });
            } finally {
                setUploading(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }
        },
        [showToast, fetchSuppliers]
    );

    const handleRetry = useCallback(() => {
        fetchSuppliers();
    }, [fetchSuppliers]);

    const handleExcelDownload = useCallback(() => {
        if (suppliers.length === 0) {
            showToast('다운로드할 데이터가 없습니다.', { type: ToastType.WARNING });
            return;
        }

        const columns: ExcelColumn<SupplierDetail>[] = [
            { header: '협력사구분', key: 'suppType' },
            { header: '등급', key: 'grade' },
            { header: '협력사명', key: 'name' },
            { header: 'DP코드', key: 'id' },
            { header: '협력사연락처', key: 'phoneNumber' },
            { header: '지점명', key: 'name' },
            { header: '요금제', key: 'ratePlan' },
            { header: '등록일자', key: 'registrationDate' },
            { header: '운영종료일자', key: 'endDate' },
            { header: '사업주명', key: 'ownerName' },
            { header: '상호명', key: 'companyName' },
            { header: '사업자등록번호', key: 'businessNumber' },
            { header: '예금주', key: 'accountHolder' },
            { header: '은행명', key: 'bankName' },
            { header: '계좌번호', key: 'accountNumber' },
            { header: '이메일주소', key: 'email' },
            { header: '사업자소재지', key: 'address' },
            { header: '업태', key: 'bizType' },
            { header: '종목', key: 'bizItem' },
            { header: '과세자', key: 'taxType' },
        ];

        const filename = `협력사현황_${getTimestampForFilename()}`;
        exportToExcel(suppliers, columns, filename);
        showToast('엑셀 파일이 다운로드되었습니다.', { type: ToastType.SUCCESS });
    }, [suppliers, showToast]);

    return {
        suppliers,
        allSuppliers,
        loading,
        uploading,
        error,
        filters,
        fileInputRef,
        setFilters,
        fetchSuppliers,
        handleSearch,
        handleQuickFilter,
        handleKeyDown,
        handleRowClick,
        handleUploadClick,
        handleFileChange,
        handleRetry,
        handleExcelDownload,
    };
};
